import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import { ForgottenEmailDialog } from '../components/ForgottenEmailDialog';
import { completeAuthenticatedLogin, type AuthenticationData } from '../authenticatedLogin';
import './LoginPatientPage.css';

export function LoginPatientPage() {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailHelpPointerFocus, setEmailHelpPointerFocus] = useState(false);
  const loginCardRef = useRef<HTMLElement>(null);
  const emailHelpTriggerRef = useRef<HTMLButtonElement>(null);
  const emailHelpKeyboardActivationRef = useRef(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const apiCall = API_BASE_URL + '/authenticate';
      const apiCallArg = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userName, password }),
      };

      console.log('Call ' + apiCall + ' ' + JSON.stringify(apiCallArg));

      const response = await fetch(apiCall, apiCallArg);

      if (response.ok) {
        const data = (await response.json()) as AuthenticationData;

        console.log('Data=' + JSON.stringify(data));

        if (data.tfaMode === 'email') {
          sessionStorage.setItem('pendingAuthentication', JSON.stringify({ data, userName }));
          navigate('/tfa-entry');
          return;
        }

        await completeAuthenticatedLogin(data, userName);
        navigate('/dashboard');
      } else {
        setError(`'Invalid credentials': ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      setError('Login failed, err=' + JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="login-page"
      style={{ '--login-background-image': "url('/images/StartImage.jpg')" } as React.CSSProperties}
      onPointerDownCapture={() => setEmailHelpPointerFocus(true)}
      onKeyDownCapture={() => setEmailHelpPointerFocus(false)}
    >
      <section ref={loginCardRef} className="login-page__card" aria-labelledby="login-heading">
        <h1 className="login-page__practice text-slate-900">
          <span>Zahnarztpraxis </span>
          <span>Müller und Weidmann</span>
        </h1>

        <div className="login-page__intro">
          <h2 id="login-heading" className="login-page__heading text-slate-900">
            Willkommen
          </h2>
          <p className="login-page__description text-slate-500">
            Bitte melden Sie sich an, um auf Ihr Portal zuzugreifen.
          </p>
        </div>

        <form className="login-page__form" onSubmit={handleLogin}>
          <div className="login-page__field">
            <div className="login-page__label-row text-slate-700">
              <label htmlFor="username">E-Mail Adresse</label>
              <button
                ref={emailHelpTriggerRef}
                type="button"
                className="login-page__help-link"
                aria-haspopup="dialog"
                aria-controls="forgotten-email-dialog"
                data-pointer-focus={emailHelpPointerFocus ? 'true' : undefined}
                onClick={(event) => {
                  emailHelpKeyboardActivationRef.current = event.detail === 0;
                  setEmailHelpPointerFocus(event.detail > 0);
                  setEmailDialogOpen(true);
                }}
              >
                E-Mail Adresse vergessen
              </button>
            </div>
            <input
              type="text"
              id="username"
              value={userName}
              onChange={(event) => setUserName(event.target.value)}
              required
              className="login-page__input text-slate-700"
            />
          </div>

          <div className="login-page__field">
            <div className="login-page__label-row text-slate-700">
              <label htmlFor="password">Passwort</label>
              <button type="button" onClick={() => navigate('/forgotten-password')} className="login-page__help-link">
                Passwort vergessen
              </button>
            </div>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              className="login-page__input text-slate-700"
            />
          </div>

          {error && (
            <div className="login-page__error text-sm text-red-600" role="alert">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="login-page__submit text-white">
            Anmelden
          </button>
        </form>

        <div className="login-page__activation text-slate-500">
          <Link to="/registration-code" className="login-page__help-link login-page__registration-link">
            <svg viewBox="0 0 24 24" className="login-page__registration-icon" fill="none" aria-hidden="true">
              <circle cx="9" cy="6" r="4" stroke="currentColor" strokeWidth="1.6" />
              <path
                d="M14.8 21H3v-6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="17.5" cy="18.5" r="5.2" fill="currentColor" />
              <path d="M17.5 15.8v5.4M14.8 18.5h5.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <span>Registrieren</span>
          </Link>
        </div>
      </section>
      {emailDialogOpen && (
        <ForgottenEmailDialog
          anchorRef={loginCardRef}
          onClose={() => setEmailDialogOpen(false)}
          returnFocusRef={emailHelpTriggerRef}
          openedWithKeyboard={emailHelpKeyboardActivationRef.current}
        />
      )}
    </main>
  );
}
